import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Maximize2, X } from "lucide-react";
import SEO from "@/components/SEO";
import { AI_FORMULA } from "@/data/aiFormula";
import "./TheClub.css";

const SLIDES = [
  { id: "welcome", title: "Manju Abraham and Todd Larsen" },
  { id: "ai-formula", title: "The AI Prompt Formula" },
  { id: "use-cases", title: "Put the formula to work" },
  { id: "next-steps", title: "Get the Fortune 100 Prompt Library" },
] as const;

// The same seven segments, quotation marks, tones and key as /ai-30/program.
const FORMULA_SEGMENTS = [
  { text: "“How might we", tone: "neutral" },
  { text: "VERB", tone: "orange" },
  { text: "OUTCOME", tone: "blue" },
  { text: "without", tone: "neutral" },
  { text: "CONSTRAINTS", tone: "green" },
  { text: "based on", tone: "neutral" },
  { text: "SOURCES”", tone: "yellow" },
];
const FORMULA_KEY = [
  { text: "Action", tone: "orange" },
  { text: "Definition of done", tone: "blue" },
  { text: "Rules + boundaries", tone: "green" },
  { text: "Sources", tone: "yellow" },
];
const EXAMPLES = [
  {
    title: "Chief of Staff",
    leadIn: "how might we",
    verb: "create",
    outcome: "a chief of staff agent that runs hourly to monitor my inboxes, triages priorities, and draft work for me",
    constraints: "",
    sources: "email, calendar, clickup, and github",
    questionMark: false,
  },
  {
    title: "Career",
    verb: "tailor",
    outcome: "my resume for this role",
    constraints: "inventing experience",
    sources: "my resume and the job description",
  },
  {
    title: "Business idea",
    verb: "design",
    outcome: "a 30-day test for my consulting offer",
    constraints: "assuming demand or committing spend",
    sources: "my experience and customer interview notes",
  },
  {
    title: "Time saved",
    verb: "estimate",
    outcome: "my net hours saved on completed work last week as a range",
    constraints: "double-counting work or ignoring prompting, review, and rework time",
    sources: "all chat sessions and estimated average employee time for comparable tasks",
  },
];
const LIBRARY_SIGNUP = "https://www.technical-leaders.com/library#library-signup";
const LEGACY_SLIDES: Record<string, string> = {
  "how-might-we": "ai-formula",
  "outcome-first-example": "use-cases",
  "career-momentum": "use-cases",
  "business-idea": "use-cases",
  "keep-practicing": "next-steps",
};

function SlideContent({ id }: { id: string }) {
  if (id === "welcome") return <div className="club4-cover">
    <div className="club4-logos">
      <img className="club4-club-logo" src="/the-club/club-logo.jpg" width="431" height="126" alt="The CLUB" />
      <img className="club4-tech-logo" src="/transparent-bg-logo.png" width="754" height="130" alt="Tech Leaders" />
    </div>
    <h1 className="club4-names"><span>Manju Abraham</span><span>Todd Larsen</span></h1>
  </div>;

  if (id === "ai-formula") return <div className="club4-content">
    <h1 className="club4-title">The AI Prompt Formula</h1>
    <div className="club4-formula" role="group" aria-label={AI_FORMULA}>
      {FORMULA_SEGMENTS.map((segment) => <span key={segment.text} className="club4-segment" data-tone={segment.tone}>{segment.text}</span>)}
    </div>
    <div className="club4-key">{FORMULA_KEY.map((item) => <span key={item.text} data-tone={item.tone}>{item.text}</span>)}</div>
  </div>;

  if (id === "use-cases") return <div className="club4-content">
    <h1 className="club4-title">Put the formula to work.</h1>
    <ol className="club4-examples">{EXAMPLES.map((example) => <li key={example.title}>
      <h2>{example.title}</h2>
      <p>“{example.leadIn ?? "How might we"} <span className="club4-example-part" data-tone="orange">{example.verb}</span>{" "}<span className="club4-example-part" data-tone="blue">{example.outcome}</span>{example.constraints && <> without <span className="club4-example-part" data-tone="green">{example.constraints}</span></>} based on <span className="club4-example-part" data-tone="yellow">{example.sources}</span>{example.questionMark === false ? "" : "?"}”</p>
    </li>)}</ol>
  </div>;

  return <div className="club4-content club4-next-layout">
    <div className="club4-next-copy">
      <p className="club4-eyebrow">Next steps</p>
      <h1 className="club4-title">Get the Fortune 100 Prompt Library.</h1>
      <p className="club4-next-lead">Scan the code, enter your email, and get the library link.</p>
      <a className="club4-library-link" href={LIBRARY_SIGNUP} target="_blank" rel="noreferrer">technical-leaders.com/library <ArrowRight size={20} aria-hidden="true" /></a>
    </div>
    <div className="club4-qr">
      <a href={LIBRARY_SIGNUP} target="_blank" rel="noreferrer"><img src="/the-club/library-qr.svg" width="410" height="410" alt="Scan to get the Fortune 100 Prompt Library" /></a>
      <p className="club4-qr-caption">100 practical AI workflows.</p>
    </div>
  </div>;
}

export default function TheClub() {
  // The server and first client render share the opening slide.
  const [index, setIndex] = useState(0);
  const [ready, setReady] = useState(false);
  const [notice, setNotice] = useState("");
  const deckRef = useRef<HTMLElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const slideRef = useRef<HTMLElement>(null);
  const touchStart = useRef({ x: 0, y: 0 });
  const slide = SLIDES[index];

  const navigate = useCallback((next: number) => {
    setIndex(Math.max(0, Math.min(SLIDES.length - 1, next)));
    setNotice("");
    requestAnimationFrame(() => {
      stageRef.current?.scrollTo(0, 0);
      slideRef.current?.focus({ preventScroll: true });
    });
  }, []);

  useEffect(() => {
    const readLocation = () => {
      const requested = window.location.hash.slice(1);
      const id = LEGACY_SLIDES[requested] ?? requested;
      const found = SLIDES.findIndex((entry) => entry.id === id);
      const next = found < 0 ? 0 : found;
      const params = new URLSearchParams(window.location.search);
      params.delete("cases");
      const query = params.toString();
      const search = query ? `?${query}` : "";
      window.history.replaceState(null, "", `${window.location.pathname}${search}#${SLIDES[next].id}`);
      navigate(next);
    };
    readLocation();
    setReady(true);
    window.addEventListener("hashchange", readLocation);
    window.addEventListener("popstate", readLocation);
    return () => {
      window.removeEventListener("hashchange", readLocation);
      window.removeEventListener("popstate", readLocation);
    };
  }, [navigate]);

  useEffect(() => {
    if (ready && window.location.hash !== `#${slide.id}`) {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#${slide.id}`);
    }
  }, [ready, slide.id]);

  const toggleFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else if (deckRef.current?.requestFullscreen) await deckRef.current.requestFullscreen();
      else setNotice("Use your browser’s full screen command.");
    } catch { setNotice("Use your browser’s full screen command."); }
    slideRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (event.altKey || event.ctrlKey || event.metaKey || target.closest("input, textarea, select, [contenteditable='true']")) return;
      if (["ArrowRight", "PageDown"].includes(event.key) || (event.key === " " && !target.closest("a, button"))) {
        event.preventDefault(); navigate(index + 1);
      } else if (["ArrowLeft", "PageUp"].includes(event.key)) {
        event.preventDefault(); navigate(index - 1);
      } else if (event.key === "Home" || event.key === "End") {
        event.preventDefault(); navigate(event.key === "Home" ? 0 : SLIDES.length - 1);
      } else if (event.key.toLowerCase() === "f") {
        event.preventDefault(); void toggleFullscreen();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [index, navigate, toggleFullscreen]);

  return <main className="club4-deck" data-current-slide={slide.id} ref={deckRef}>
    <SEO title="The CLUB × Tech Leaders" description="Manju Abraham and Todd Larsen: the AI Prompt Formula, practical examples, and the Fortune 100 Prompt Library." robots="noindex, follow" />
    <div className="club4-stage" ref={stageRef}>
      <section className={`club4-slide club4-${slide.id}`} data-slide-id={slide.id} ref={slideRef} tabIndex={-1} aria-label={`Slide ${index + 1} of ${SLIDES.length}: ${slide.title}`}
        onTouchStart={(event) => { const touch = event.changedTouches[0]; touchStart.current = { x: touch.clientX, y: touch.clientY }; }}
        onTouchEnd={(event) => {
          if ((event.target as HTMLElement).closest("a, button") || window.getSelection()?.toString()) return;
          const touch = event.changedTouches[0];
          const dx = touch.clientX - touchStart.current.x, dy = touch.clientY - touchStart.current.y;
          if (Math.abs(dx) > 70 && Math.abs(dx) > Math.abs(dy) * 1.5) navigate(index + (dx < 0 ? 1 : -1));
        }}>
        <SlideContent id={slide.id} />
      </section>
    </div>
    <footer className="club4-nav">
      <span className={index === 0 ? "club4-sr-only" : "club4-count"}>{index + 1} / {SLIDES.length}</span>
      <div className="club4-nav-buttons">
        <button type="button" aria-label="Previous slide" onClick={() => navigate(index - 1)} disabled={index === 0}><ArrowLeft size={20} /></button>
        <button type="button" aria-label="Next slide" onClick={() => navigate(index + 1)} disabled={index === SLIDES.length - 1}><ArrowRight size={20} /></button>
        <button type="button" aria-label="Toggle fullscreen" title="Fullscreen (F)" onClick={() => void toggleFullscreen()}><Maximize2 size={18} /></button>
      </div>
    </footer>
    {notice && <button type="button" className="club4-notice" role="status" onClick={() => setNotice("")}>{notice}<X size={16} aria-hidden="true" /></button>}
  </main>;
}
